import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react'
import { cn } from '@/lib/cn.ts'

/**
 * The Material 3 filled text field: rounded top corners, square bottom, and a
 * 2px bottom border that turns primary on focus. The shape is the point — a
 * uniformly rounded input reads as a different design system entirely.
 */
const FIELD_SHELL =
  'h-14 w-full rounded-t-sm border-b-2 bg-surface-muted px-4 text-body-md text-on-surface ' +
  'border-outline transition-colors duration-200 outline-none ' +
  'focus:border-primary disabled:opacity-50'

interface FieldFrameProps {
  id: string
  label: string
  hint?: ReactNode
  error?: string | undefined
  children: ReactNode
  className?: string
}

function FieldFrame({ id, label, hint, error, children, className }: FieldFrameProps) {
  return (
    <div className={cn('w-full', className)}>
      <label htmlFor={id} className="mb-1.5 block text-label-md font-medium text-on-surface-variant">
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1.5 text-label-sm text-error">{error}</p>
      ) : hint ? (
        <p className="mt-1.5 text-label-sm text-on-surface-variant">{hint}</p>
      ) : null}
    </div>
  )
}

export interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | undefined
  /** Rendered inside the field, e.g. a currency symbol. */
  prefix?: string
  containerClassName?: string
}

export function TextField({
  label,
  hint,
  error,
  prefix,
  containerClassName,
  className,
  ...props
}: TextFieldProps) {
  const id = useId()

  return (
    <FieldFrame
      id={id}
      label={label}
      hint={hint}
      error={error}
      className={containerClassName}
    >
      <div className="relative">
        {prefix ? (
          <span
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-body-md text-on-surface-variant"
            aria-hidden="true"
          >
            {prefix}
          </span>
        ) : null}
        <input
          id={id}
          aria-invalid={error ? true : undefined}
          className={cn(
            FIELD_SHELL,
            prefix && 'pl-9',
            error && 'border-error focus:border-error',
            className,
          )}
          {...props}
        />
      </div>
    </FieldFrame>
  )
}

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string
  hint?: ReactNode
  error?: string | undefined
  containerClassName?: string
}

export function SelectField({
  label,
  hint,
  error,
  containerClassName,
  className,
  children,
  ...props
}: SelectFieldProps) {
  const id = useId()

  return (
    <FieldFrame id={id} label={label} hint={hint} error={error} className={containerClassName}>
      <select id={id} className={cn(FIELD_SHELL, 'pr-10', className)} {...props}>
        {children}
      </select>
    </FieldFrame>
  )
}

export interface SwitchFieldProps {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

export function SwitchField({
  label,
  description,
  checked,
  onChange,
  disabled,
}: SwitchFieldProps) {
  return (
    <label className="flex cursor-pointer items-start justify-between gap-4 py-2">
      <span className="min-w-0">
        <span className="block text-body-md text-on-surface">{label}</span>
        {description ? (
          <span className="mt-0.5 block text-label-sm text-on-surface-variant">{description}</span>
        ) : null}
      </span>
      <span className="relative shrink-0 pt-1">
        <input
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />
        <span
          aria-hidden="true"
          className={cn(
            'block h-8 w-13 rounded-full border-2 transition-colors duration-200',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',
            checked ? 'border-primary bg-primary' : 'border-outline bg-surface-muted',
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            'absolute top-2.5 left-1 block size-5 rounded-full transition-all duration-200 ease-emphasized',
            checked ? 'translate-x-5 bg-on-primary' : 'translate-x-0 bg-outline',
          )}
        />
      </span>
    </label>
  )
}

export interface DateFieldProps {
  label: string
  value: string
  min?: string | undefined
  max?: string | undefined
  onChange: (value: string) => void
  containerClassName?: string
}

/** A `YYYY-MM-DD` picker. An empty value means the caller treats it as unset. */
export function DateField({
  label,
  value,
  min,
  max,
  onChange,
  containerClassName,
}: DateFieldProps) {
  const id = useId()

  return (
    <FieldFrame id={id} label={label} className={cn('sm:w-48', containerClassName)}>
      <input
        id={id}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(event) => onChange(event.target.value)}
        className={FIELD_SHELL}
      />
    </FieldFrame>
  )
}
